import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, Col, Row } from 'reactstrap';
import I18n from '../../blocks/localization';
import DateUtility from '../../blocks/utils/date-utility';
import { connect, useSelector } from 'react-redux';
import { ApplicationState } from '../../store';
import NumberUtility from '../../blocks/utils/number-utility';
import ContentLayout from '../core/ContentLayout';
import PrometTable from '../shared/promet-table/PrometTable';
import { TicketListItem } from '../../models/ticket/TicketListItem';
import { getDocumentActions } from '../../store/document/actions';
import RoutesConst from '../../blocks/consts/RoutesConst';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import ReceiptOutlinedIcon from '@material-ui/icons/ReceiptOutlined';
import ForumOutlinedIcon from '@material-ui/icons/ForumOutlined';
import ConfirmationNumberOutlinedIcon from '@material-ui/icons/ConfirmationNumberOutlined';
import right_arrow from '../../assets/icons/1x/right-arrow.png';
import coupon_enabled from '../../assets/icons/2x/coupon_enabled.png';
import PrometSelect from '../shared/PrometSelect';
import Ddl from '../../models/common/Ddl';
import { CouponStatusType } from '../../models/coupon/CouponStatusType';
import Coupon from '../../models/coupon/Coupon';
import EWalletPredefinedValueUtility from '../../blocks/utils/eWallet-predefined-value-utility/EWalletPredefinedValueUtility';
import Toast from '../shared/Toast';
import EWalletUtility from '../../blocks/utils/e-wallet-utility/EWalletUtility';
import Faq from '../../models/faq/Faq';
import { getDashboard } from '../../store/application/actions';
import EuroUtility from '../../blocks/utils/euro-utility';
import CurrencyUtility from '../../blocks/utils/currency-utility/CurrencyUtility';
import EWalletConst from '../../blocks/consts/EWalletConst';

type Props = typeof mapDispatchToProps;

const Dashboard = (props: Props) => {
    const [topUpAmount, setTopUpAmount] = useState<number>(EWalletUtility.getPredefinedValues()[0]);

    const account = useSelector((state: ApplicationState) => state.account.account);
    const client = useSelector((state: ApplicationState) => state.client.client);
    const transactions = useSelector((state: ApplicationState) => state.ticket.latestTransactions);
    const coupons = useSelector((state: ApplicationState) => state.client.coupons);
    const faqNotifications = useSelector((state: ApplicationState) => state.faq.notifications) ?? [];
    const faqOnboardingMessage = useSelector((state: ApplicationState) => state.faq.onboardingMessage)?.flat()??[];

    const primaryCurrency = CurrencyUtility.getPrimary();
    const secondaryCurrency = CurrencyUtility.getSecondary();
    const maxEWalletAmount = EWalletConst.getMaxEWalletAmount();
    
    const validCoupons = coupons?.filter(x => x.status === CouponStatusType.CurrentlyValid || x.status === CouponStatusType.NotYetValid);   

    const faqMessages = faqOnboardingMessage.concat(faqNotifications);

    const history = useHistory();

    useEffect(() => {
        props.getDashboard();
    }, []);

    const redirect = (route: string, state: any = null) => {
        history.push(route, state);
    }

    const onTopUpAmountChange = (name: any, value: any) => {
        setTopUpAmount(value);
    }

    const onActivateFullProfileClick = () => {
        if (account?.isFullProfileActivationInProgress) {
            Toast.notifyInfo(I18n.t('activateFullProfile.processStarted'));
            return;
        }
        redirect(RoutesConst.ActivateFullProfile);
    }

    const onTopUpClick = () => {
        redirect(RoutesConst.EWallet, { topUpAmount });
    }

    const getEPurseAmount = (): number => {
        return EWalletUtility.ePurseAmount(account, client);
    }

    const getEWalletTopUpOptions = (): Ddl[] => {
        const options = EWalletPredefinedValueUtility.getPredefinedValuesBelowLimit(getEPurseAmount())
            .map(predefinedValue => ({ id: predefinedValue, value: `${predefinedValue} ${primaryCurrency.sign} (${EuroUtility.convert(predefinedValue)} ${secondaryCurrency.sign})` } as Ddl));

        return options;
    }

    const getTicketPeriodName = (validFrom?: Date, validTo?: Date): string => {
        if (new Date(String(validFrom))?.getMonth() === new Date(String(validTo))?.getMonth() && new Date(String(validFrom))?.getFullYear() === new Date(String(validTo))?.getFullYear())
            return I18n.t('periodNameMontly');

        return I18n.t('periodNameYearly');
    }

    const downloadPdf = (documentContentToken: string) => {
        props.getDocument(documentContentToken);
    }

    const eWalletTopUpOptions = getEWalletTopUpOptions();
    const isEWalletTopUpEnabled = account && eWalletTopUpOptions.length > 0;

    return (
        <ContentLayout titleTranslateId='dashboard.title'>
            <Row>
                <Col xl={4} md={6} sm={12} className="mb-4">
                    <div id="dashboardAccountPurseState" className="promet-card">
                        <div id="dashboardAccountPurseStateHeader" className="promet-card-title d-flex justify-content-between">
                            <span id="dashboardAccountPurseStateHeaderTitle">
                                {I18n.t('dashboard.accountStateTitle')}
                            </span>
                            <span id="dashboardAccountPurseStateHeaderDate">
                                {DateUtility.format(new Date())}
                            </span>
                        </div>
                        <div id="dashboardAccountPurseStateAmount">
                            {`${NumberUtility.format(getEPurseAmount() || 0, 2, true, false, '.')} ${primaryCurrency.sign}`}
                        </div>
                        <div className='euro-text'>
                            {`(${EuroUtility.convert(getEPurseAmount())} ${secondaryCurrency.sign})`}
                        </div>
                        <div id="dashboardAccountPurseStateTopUpText">
                            {I18n.t('dashboard.topUpText')}
                        </div>
                        <div id="dashboardAccountPurseStateTopUpSelect">
                            <PrometSelect name="topUpAmount" onChange={onTopUpAmountChange} value={topUpAmount} options={eWalletTopUpOptions} isClearable={false} isSearchable={false} isDisabled={!isEWalletTopUpEnabled} />
                        </div>
                        <div id="dashboardAccountPurseStateTopUpInfoText" className='info-text-primary' dangerouslySetInnerHTML={{ __html: I18n.t('dashboard.topUpInfoText', { primaryAmount: maxEWalletAmount, primarySign: primaryCurrency.sign, secondaryAmount: EuroUtility.convert(maxEWalletAmount), secondarySign: secondaryCurrency.sign }) }} />
                        <div className='text-center'>
                            <Button type="button" color="primary" onClick={onTopUpClick} disabled={!isEWalletTopUpEnabled}>{I18n.t('dashboard.topUpButtonText')}</Button>
                        </div>
                    </div>
                </Col>
                <Col xl={8} md={6} sm={12} className="mb-4">
                    {
                        account && !account.clientId &&
                        <div id="dashboardActivateFullAccount" className="promet-card">
                            <div id="dashboardActivateFullAccountHeader" className="promet-card-title">
                                {I18n.t('dashboard.activateFullAccountTitle')}
                            </div>
                            <div id="dashboardActivateFullAccountText1">
                                {I18n.t('dashboard.activateFullAccountText1')}
                            </div>
                            <div id="dashboardActivateFullAccountText2">
                                {I18n.t('dashboard.activateFullAccountText2Alt')}
                            </div>
                            <div>
                                <Button type="button" className='btn-akcent' color="primary" onClick={onActivateFullProfileClick}>
                                    {I18n.t('dashboard.activateFullAccountButtonText')}
                                    <img src={right_arrow} alt="" />
                                </Button>
                            </div>
                        </div>
                    }
                    {
                        account && account.clientId &&
                        <div id="dashboardCoupons" className="promet-card">
                            <div className="promet-card-title">
                                {I18n.t('dashboard.activeCouponsTitle')}
                            </div>
                            {
                                (validCoupons && !validCoupons.length) &&
                                <div className="promet-card-no-content">
                                    <div>
                                        <ConfirmationNumberOutlinedIcon />
                                        <p>{I18n.t('noActiveCoupons')}</p>
                                    </div>
                                </div>
                            }
                            {
                                validCoupons && validCoupons.length > 0 &&
                                <PrometTable
                                    data={validCoupons}
                                    columns={
                                        [
                                            {
                                                cell: (d: Coupon) => (
                                                    <div>
                                                        <div className='dashboardCouponsIcon float-left'>
                                                            <img src={coupon_enabled} alt="" />
                                                        </div>
                                                        <div className='dashboardCouponsContent float-left'>
                                                            <div className='dashboardCouponsItemPeriodName'>
                                                                {getTicketPeriodName(d.validFrom, d.validTo)}
                                                            </div>
                                                            <div className='dashboardCouponsContentRelationPrice'>
                                                                <span className='dashboardCouponsItemRelation float-left'>
                                                                    {d.relation.caption}
                                                                </span>
                                                                <span className='dashboardCouponsItemPrice float-right'>
                                                                    {`(${EuroUtility.convertCurrency(d.price, d.currencyId)} ${CurrencyUtility.getSecondaryCurrencyById(d.currencyId).sign})`}
                                                                </span>
                                                                <span className='dashboardCouponsItemPrice float-right mr-1'>
                                                                    {`${NumberUtility.format(d.price || 0, 2, true, false, ',')} ${CurrencyUtility.getPrimaryCurrencyById(d.currencyId).sign}`}
                                                                </span>
                                                                <div className='clearfix'></div>
                                                            </div>
                                                            <div className='dashboardCouponsItemPeriod'>
                                                                {I18n.t('validFrom')}: {DateUtility.format(d.validFrom, 'dd/mm/yyyy')} - {DateUtility.format(d.validTo, 'dd/mm/yyyy')}
                                                            </div>
                                                        </div>

                                                        <div className='clearfix'></div>
                                                    </div>
                                                ),
                                                align: 'left'
                                            }
                                        ]
                                    }
                                    hideFooter
                                    hideHeader
                                />
                            }
                            <div>
                                <Button type="button" color="primary" outline onClick={() => redirect(RoutesConst.MyCoupons)}>{I18n.t('dashboard.buyCoupon')}</Button>
                            </div>
                        </div>
                    }
                </Col>
            </Row>
            <Row>
                <Col xl={6} md={12} className="mb-4">
                    <div id="dashboardTransactions" className="promet-card">
                        <div className="promet-card-title">
                            {I18n.t('dashboard.latestTransactionsTitle')}
                        </div>
                        {
                            (transactions && !transactions.length) &&
                            <div className="promet-card-no-content">
                                <div>
                                    <ReceiptOutlinedIcon />
                                    <p>{I18n.t('noTransactionsCurrently')}</p>
                                </div>
                            </div>
                        }
                        {
                            transactions && transactions.length > 0 &&
                            <PrometTable
                                data={transactions}
                                columns={
                                    [
                                        { accessor: (d: TicketListItem) => DateUtility.formatWithTime(d.created), align: 'left' },
                                        { accessor: (d: TicketListItem) => d.name, align: 'left', className: "font-weight-500" },
                                        { accessor: (d: TicketListItem) => <span>{`${d.price > 0 ? '+' : ''}${NumberUtility.format(d.price, 2, true, false, ',')} ${CurrencyUtility.getPrimaryCurrencyById(d.currencyId).sign} (${EuroUtility.convertCurrency(d.price, d.currencyId)} ${CurrencyUtility.getSecondaryCurrencyById(d.currencyId).sign})`}</span>, align: 'right', className: "font-weight-500 text-nowrap" },
                                        {
                                            cell: (d: TicketListItem) => (
                                                <>
                                                    {
                                                        d.documentContentToken &&
                                                        <span onClick={() => downloadPdf(d.documentContentToken)} className="cursor-pointer">
                                                            <PictureAsPdfIcon className="icon-pdf" />
                                                        </span>
                                                    }
                                                </>
                                            ),
                                            align: 'right',
                                            visible: transactions.filter(x => x.documentContentToken).length > 0
                                        }
                                    ]
                                }
                                hideFooter
                                hideHeader
                            />
                        }
                    </div>
                </Col>
                <Col xl={6} md={12}>
                    <div id="dashboardNotifications" className="promet-card">
                        <div className="promet-card-title">
                            {I18n.t('dashboard.messagesAndNotificationsTitle')}
                        </div>
                        {
                            (faqMessages && !faqMessages.length) &&
                            <div className="promet-card-no-content">
                                <div>
                                    <ForumOutlinedIcon />
                                    <p>{I18n.t('noNotificationsCurrently')}</p>
                                </div>
                            </div>
                        }
                        {
                            faqMessages && faqMessages.length > 0 &&
                            <PrometTable
                                data={faqMessages}
                                columns={
                                    [
                                        {
                                            cell: (d: Faq) => (
                                                <div>
                                                    <h6>{d?.title}</h6>
                                                    <div dangerouslySetInnerHTML={{ __html: d?.htmlContent }} />
                                                </div>
                                            ),
                                            align: 'left'
                                        }
                                    ]
                                }
                                hideFooter
                                hideHeader
                            />
                        }
                    </div>
                </Col>
            </Row>
        </ContentLayout>
    );
}

const mapDispatchToProps = {
    getDashboard: getDashboard.request,
    getDocument: getDocumentActions.request
};

export default connect(null, mapDispatchToProps)(Dashboard);
